import { InputActions } from "@common/constants";
import { isStoreItemId, STORE_LIMITS, type StoreItemView } from "@common/platform/store";
import { Game } from "../game";
import { InputManager } from "../managers/inputManager";
import { t } from "../utils/translations/phrases";
import { StoreCatalog } from "./storeCatalog";
import { StoreMenu } from "./storeMenu";

/*
 * The voice reactions' wheel in a match (0.92): hold the reaction key (X since 0.93), point at one, let go and it's said (the
 * players nearby hear it); on a phone, the reactions button opens it and a tap says one (a tap anywhere else closes it).
 * Only the reactions the player took into the match (bought and worn: the server checks again), one every few seconds,
 * and only while playing: it closes when the player dies, watches or leaves (0.93 review).
 */

let root: HTMLDivElement | undefined;
let shown = false;
let tapMode = false;
let origin = { x: 0, y: 0 };
let selected = -1;
let lastSaidAt = Number.NEGATIVE_INFINITY;
let slots: ReadonlyArray<{ readonly id: string, readonly text: string }> = [];
let hintTimer: ReturnType<typeof setTimeout> | undefined;
/**
 * the press on the backdrop that closed it, until its release has gone by: let go on the phone's reactions button
 * (under the backdrop), as a mouse's or a trackpad's is, it doesn't open the wheel again. Let go anywhere else, the next
 * press on the button opens it at once (0.93 review)
 */
let closedByPress: { readonly pointerId: number, readonly at: number } | undefined;

/**
 * until when the click that follows a press the wheel took (a reaction, or the backdrop) is swallowed: the wheel is gone
 * by the time it comes, and on a phone it landed on what was under the finger, the controls under the wheel taking it as
 * theirs (the interact message picked up loot, opened a door or cancelled the healing; the ammo counter reloaded: 0.94
 * audit). One click only
 */
let swallow: { readonly until: number, readonly pointerId: number } | undefined;
const swallowClick = (event: MouseEvent): void => {
    const armed = swallow;
    if (!armed || performance.now() > armed.until) return;
    // (a click no pointer made, a key's, goes through; the review of the fix. A pointer's is never told apart by its id:
    // browsers don't all give a tap's click its touch's)
    const click = event as Partial<PointerEvent>;
    if (click.pointerType === "" || (click.pointerId !== undefined && click.pointerId < 0)) return;
    swallow = undefined;
    event.preventDefault();
    event.stopImmediatePropagation();
};
/** a press the wheel took: the click that follows it is swallowed (a main button's or a touch's: no other makes one) */
const swallowAfter = (event: PointerEvent): void => {
    swallow = event.button === 0 ? { until: performance.now() + 700, pointerId: event.pointerId } : undefined;
};
/**
 * the pointer and the button whose press the wheel took: its release is the wheel's too (a mouse's landed on what was
 * under it). A mouse is one pointer whatever its buttons: only that button's release (another's, an attack held, is the
 * game's: it was stopped, and the gun kept firing; the review of the fix)
 */
let pressTaken: { readonly pointerId: number, readonly button: number } | undefined;
/** a mouse's buttons as event.buttons has them, by event.button (left, middle, right, back, forward) */
const BUTTON_BITS = [1, 4, 2, 8, 16];
/** the taken button let go another way (with another held, the browser says it as a move), or a new press: forgotten */
const forgetTaken = (event: PointerEvent): void => {
    const taken = pressTaken;
    if (!taken || taken.pointerId !== event.pointerId) return;
    if (event.type === "pointerdown" || (event.pointerType === "mouse" && (event.buttons & (BUTTON_BITS[taken.button] ?? 0)) === 0)) pressTaken = undefined;
};

/**
 * the closing press let go, wherever: forgotten once what it landed on has had it (the button lets it go by). A press the
 * wheel took is let go nowhere else
 */
const released = (event: PointerEvent): void => {
    const taken = pressTaken;
    if (taken?.pointerId === event.pointerId && (event.pointerType !== "mouse" || event.button === taken.button)) {
        pressTaken = undefined;
        event.stopImmediatePropagation();
    }
    // (a press the browser took for a scroll makes no click: none is swallowed after it)
    if (event.type === "pointercancel" && swallow?.pointerId === event.pointerId) swallow = undefined;
    const press = closedByPress;
    if (press?.pointerId !== event.pointerId) return;
    setTimeout(() => {
        if (closedByPress === press) closedByPress = undefined;
    }, 0);
};

/**
 * the reactions this match takes from the player, in the wheel's order: its game ticket's, which the server took them
 * from (undefined: not known, the ones the menu knows it wears are offered). Worn reactions changed on another device
 * or in another tab used to be offered, the server saying nothing of them, and the match's own left out (0.94 audit)
 */
let matchReactions: readonly string[] | undefined;

/** a game ticket's reactions (its body is the server's own words, signed); undefined: it can't be read */
const ticketReactions = (ticket: string): readonly string[] | undefined => {
    try {
        const body = (ticket.split(".")[0] ?? "").replace(/-/gu, "+").replace(/_/gu, "/");
        const text = atob(body.padEnd(Math.ceil(body.length / 4) * 4, "="));
        const payload = JSON.parse(new TextDecoder().decode(Uint8Array.from(text, char => char.charCodeAt(0)))) as { readonly reactions?: unknown };
        return Array.isArray(payload.reactions) ? payload.reactions.filter((id): id is string => typeof id === "string" && isStoreItemId(id)) : [];
    } catch {
        return undefined;
    }
};

const escapeHtml = (text: string): string => text.replace(/[&<>"']/gu, char => `&#${char.charCodeAt(0)};`);

const build = (): HTMLDivElement => {
    const element = document.createElement("div");
    element.id = "reaction-wheel";
    element.hidden = true;
    element.setAttribute("role", "menu");
    element.setAttribute("aria-label", t("Voice reactions"));
    element.addEventListener("pointerdown", event => {
        const slot = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-slot]");
        if (!slot) {
            // (the backdrop, in tap mode: a tap off the wheel closes it, saying nothing)
            if (tapMode) {
                event.preventDefault();
                swallowAfter(event);
                pressTaken = { pointerId: event.pointerId, button: event.button };
                ReactionWheel.hide(false);
                closedByPress = { pointerId: event.pointerId, at: performance.now() };
            }
            return;
        }
        event.preventDefault();
        swallowAfter(event);
        pressTaken = { pointerId: event.pointerId, button: event.button };
        selected = Number(slot.dataset.slot);
        ReactionWheel.hide(true);
    });
    // held open with a mouse button (Settings → Keybinds): let go over the wheel, it closes, saying the reaction let go
    // over (or pointed at). The release lands on the wheel, never on the game, which closes the wheel on it: nothing was
    // said and the wheel stayed open (0.94 audit). Only that button's release: another let go over it (an attack held
    // as the wheel opened) says nothing, and a key's release isn't a pointer's (a reaction pressed is said as it's
    // pressed, above)
    element.addEventListener("pointerup", event => {
        if (tapMode || !shown || event.pointerType !== "mouse") return;
        if (!InputManager.binds.getInputsBoundToAction("+reaction_wheel").includes(`Mouse${event.button}`)) return;
        const slot = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-slot]");
        if (slot) selected = Number(slot.dataset.slot);
        ReactionWheel.hide(true);
    });
    // (seen first, wherever it lands: a touch's comes back to the backdrop that took its press, a mouse's to what's under it)
    window.addEventListener("pointerup", released, true);
    window.addEventListener("pointercancel", released, true);
    window.addEventListener("pointerdown", forgetTaken, true);
    window.addEventListener("pointermove", forgetTaken, true);
    window.addEventListener("blur", () => {
        pressTaken = undefined;
        swallow = undefined;
    });
    window.addEventListener("click", swallowClick, true);
    document.body.appendChild(element);
    return element;
};

const onMove = (event: PointerEvent): void => {
    if (!shown || tapMode || !root) return;
    const dx = event.clientX - origin.x;
    const dy = event.clientY - origin.y;
    // (a little way from the middle first: no reaction for a twitch)
    if (Math.hypot(dx, dy) < 28 || !slots.length) selected = -1;
    else {
        const step = (Math.PI * 2) / slots.length;
        // slot 0 straight up, then round clockwise
        const angle = (Math.atan2(dy, dx) + Math.PI / 2 + Math.PI * 2 + step / 2) % (Math.PI * 2);
        selected = Math.floor(angle / step) % slots.length;
    }
    root.querySelectorAll<HTMLElement>("[data-slot]").forEach(slot => slot.classList.toggle("on", Number(slot.dataset.slot) === selected));
};

/**
 * whether the player can react now: in a match, alive, not watching (the death replay, spectating, the end), on the
 * ground (in a plane or under a chute the server says nothing: a reaction chosen up there used up its wait for none,
 * 1.06.1's audit)
 */
const playing = (): boolean => Game.gameStarted && !Game.gameOver && !Game.spectating && !Game.delayedView && Game.activePlayer?.dead !== true && Game.activePlayer?.air === undefined;

export const ReactionWheel = {
    get enabled(): boolean {
        return shown;
    },

    set enabled(value: boolean) {
        if (value) this.show(false);
        else this.hide(true);
    },

    /** opened where the pointer is (`tap`: in the middle of the screen, a tap says one) */
    show(tap: boolean): void {
        if (shown || !playing()) return;
        root ??= build();
        tapMode = tap;
        slots = (matchReactions ?? StoreMenu.reactions)
            .map(id => StoreCatalog.item(id))
            .filter((item): item is StoreItemView => item?.kind === "reaction")
            .map(item => ({ id: item.id, text: item.text ?? item.name }))
            .slice(0, STORE_LIMITS.maxReactionsWorn);
        const cooling = performance.now() - lastSaidAt < STORE_LIMITS.reactionCooldownMs;
        origin = tap ? { x: window.innerWidth / 2, y: window.innerHeight / 2 } : { x: InputManager.mousePosition.x || window.innerWidth / 2, y: InputManager.mousePosition.y || window.innerHeight / 2 };
        root.style.left = `${origin.x}px`;
        root.style.top = `${origin.y}px`;
        root.classList.toggle("cooling", cooling);
        root.classList.toggle("tap", tap);
        const backdrop = tap ? `<div class="reaction-backdrop"></div>` : "";
        if (!slots.length) {
            root.innerHTML = `${backdrop}<div class="reaction-hint">${t("No voice reactions yet: get some in the Store (main menu), then tick them in Mine.")}</div>`;
            root.hidden = false;
            shown = true;
            clearTimeout(hintTimer);
            hintTimer = setTimeout(() => this.hide(false), 2_500);
            return;
        }
        const radius = 112;
        root.innerHTML = `${backdrop}<div class="reaction-hub"></div>${slots.map((slot, index) => {
            const angle = (index / slots.length) * Math.PI * 2 - Math.PI / 2;
            return `<button type="button" class="reaction-slot" role="menuitem" data-slot="${index}" style="transform: translate(calc(${Math.cos(angle) * radius}px - 50%), calc(${Math.sin(angle) * radius}px - 50%))">${escapeHtml(slot.text)}</button>`;
        }).join("")}`;
        selected = -1;
        root.hidden = false;
        shown = true;
        window.addEventListener("pointermove", onMove);
    },

    /** closed: the reaction pointed at (`say`) goes out, if the last one was long enough ago and the player still plays */
    hide(say: boolean): void {
        if (!shown || !root) return;
        shown = false;
        root.hidden = true;
        window.removeEventListener("pointermove", onMove);
        clearTimeout(hintTimer);
        const slot = slots[selected];
        selected = -1;
        if (!say || !slot || !playing()) return;
        if (performance.now() - lastSaidAt < STORE_LIMITS.reactionCooldownMs) return;
        lastSaidAt = performance.now();
        InputManager.addAction({ type: InputActions.React, reaction: slot.id });
    },

    /** closed saying nothing: the match started or ended, the player died or watches */
    reset(): void {
        this.hide(false);
    },

    /**
     * the game ticket a match is joined with: its reactions are the wheel's. A reconnect's keeps the ones the match was
     * joined with (the player there is the same, and so are its reactions)
     */
    useTicket(ticket: string | null, reconnecting: boolean): void {
        // (a reconnect's ticket is what the match goes by from then on, as a page loaded again: the server takes its
        // reactions too; one that can't be read keeps what the match had)
        const reactions = ticket ? ticketReactions(ticket) : undefined;
        if (reconnecting && reactions === undefined) return;
        matchReactions = reactions;
    },

    /** the match left: the next one's ticket says what its wheel has */
    forgetMatch(): void {
        matchReactions = undefined;
    },

    /**
     * the phone's reactions button let go: opened, or closed. A press that the backdrop took (it closed the wheel) is
     * let go on the button with a mouse or a trackpad: it doesn't open the wheel again (0.93 review)
     */
    toggle(): void {
        const swallowed = closedByPress !== undefined && performance.now() - closedByPress.at < 1_000;
        closedByPress = undefined;
        if (swallowed) return;
        if (shown) this.hide(false);
        else this.show(true);
    }
};
